import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { getCrops } from '@next/arable-lib/crop';
import { CropField } from '@next/arable-lib/crop-field';
import { AuthorizationSelectors } from '@next/core-lib/authorization';
import { Store } from '@ngrx/store';
import { combineLatest, filter, map, switchMap, take, tap } from 'rxjs';

import { loadCropVarietiesByCropIdRequest } from 'src/app/advices/store/actions';
import { selectCropVarietiesByCropId } from 'src/app/advices/store/selectors';
import { CropVarietiesByCropId, SiteState } from 'src/app/shared/models';
import { UnsavedChangesService } from 'src/app/shared/services/unsaved-changes.service';
import { CropYearService } from 'src/app/shared/services/crop-year.service';
import { selectCropYear } from 'src/app/store/selectors';

import {
  GeneralQuestions,
  GeneralQuestionsRequest,
  ProcessedAnswersWithForm,
  TgaCropFieldWithStatus,
} from 'src/app/tga/shared/models/models';
import { TgaSharedService } from 'src/app/tga/shared/services/tga-shared.service';
import { fetchGeneralQuestions, loadCropFields, postGeneralQuestions } from 'src/app/tga/store/tga.actions';
import { selectCropFieldById, selectGeneralQuestions } from 'src/app/tga/store/tga.selectors';

import { SpecificGeneralQuestionsService } from './specific-general-questions.service';
import { PotatoType, TgaInputType, TGAQuestion } from '../../../models/question-models';
import { SpecificQuestionnaireComponent } from '../shared/specific-questionnaire/specific-questionnaire.component';
import { TranslatePipe } from '@next/core-lib/i18n';

@Component({
  selector: 'app-specific-general-questionnaire',
  templateUrl: './specific-general-questionnaire.component.html',
  imports: [SpecificQuestionnaireComponent, TranslatePipe],
})
export class SpecificGeneralQuestionnaireComponent implements OnInit {
  protected readonly isCropYearReadOnly = inject(CropYearService).isReadOnly;
  private readonly destroyRef = inject(DestroyRef);
  private readonly store = inject(Store<SiteState>);
  private readonly route = inject(ActivatedRoute);
  private readonly questionService = inject(SpecificGeneralQuestionsService);
  private readonly tgaSharedService = inject(TgaSharedService);
  private readonly unsavedChangesService = inject(UnsavedChangesService);

  cropFieldId: number | null = null;
  cropYear: number | null = null;
  cropField: CropField | undefined = undefined;
  cropVariety: { key: string; value: string } | undefined = undefined;

  readonly generalQuestions = signal<TGAQuestion<TgaInputType>[]>([]);
  readonly cropVarieties$ = this.store.select(selectCropVarietiesByCropId);
  readonly generalQuestions$ = this.store.select(selectGeneralQuestions);
  private readonly selectedCropYear$ = this.store.select(selectCropYear);
  readonly crops$ = this.store.select(getCrops);
  readonly isAtLeastAdvisor$ = this.store.select(
    AuthorizationSelectors.hasAtLeastAdvisorRoleForApplicationKey('TGACA'),
  );

  private readonly params$ = this.route.params.pipe(
    takeUntilDestroyed(this.destroyRef),
    map(this.questionService.extractRouteParams.bind(this.questionService)),
    filter(this.questionService.validateRouteParams.bind(this.questionService)),
    tap(({ cropFieldId, cropYear }) => {
      this.cropFieldId = cropFieldId;
      this.cropYear = cropYear;
    }),
  ) as ReturnType<typeof this.route.params.pipe<{ cropFieldId: number; cropYear: number }>>;

  readonly cropField$ = this.params$.pipe(
    switchMap((params) => this.store.select(selectCropFieldById(params.cropFieldId))),
    tap(this.handleCropFieldResult.bind(this)),
  );
  readonly varietiesForCrop$ = this.cropField$.pipe(
    tap((cropField) => {
      if (cropField?.cropField.cropId) {
        this.loadCropVarieties(cropField.cropField.cropId);
      }
    }),
    switchMap((cropField) =>
      this.store
        .select(selectCropVarietiesByCropId)
        .pipe(map((varieties) => this.mapCropVarieties(varieties, cropField?.cropField.cropId))),
    ),
  );

  readonly questions$ = combineLatest([
    this.varietiesForCrop$.pipe(filter((varieties) => varieties !== null && varieties.length > 0)),
    this.cropField$.pipe(filter((cropField) => cropField !== null)),
    this.generalQuestions$,
    this.isAtLeastAdvisor$,
  ]).pipe(
    tap(([varietiesOptions, _, __, isAtLeastAdvisor]) => {
      this.questionService.initializeGeneralQuestions(varietiesOptions, isAtLeastAdvisor);
    }),
    map(([varietiesOptions, cropField]) => {
      if (cropField?.cropField.cropVarietyId) {
        this.cropVariety = varietiesOptions?.find((v) => v.key === cropField.cropField.cropVarietyId.toString());
      }
      return this.questionService.initValues(this.cropVariety);
    }),
  );

  ngOnInit(): void {
    this.initializeParamsAndQuestions();
  }

  answersUpdated(data: ProcessedAnswersWithForm): void {
    if (this.isCropYearReadOnly()) return;

    const { answers: processedAnswers, formGroup } = data;

    if (formGroup) {
      this.unsavedChangesService.registerForm(formGroup);
    }

    let cropVarietyId: number | null = null;
    if (processedAnswers['variety']) {
      cropVarietyId = this.tgaSharedService.toNumber(processedAnswers['variety'] as string);
    }

    if (this.cropFieldId && this.cropYear) {
      const lastHarvestedYear = this.tgaSharedService.toNumber(processedAnswers['harvestYear'] as string);
      const generalQuestions: GeneralQuestions = {
        targetPurpose: processedAnswers['potatoType'] as PotatoType,
        targetExport: processedAnswers['export'] === 'yes',
        lastHarvestedYear: lastHarvestedYear,
      };
      const answersPayload: GeneralQuestionsRequest = {
        ...generalQuestions,
        cropFieldId: this.cropFieldId,
        cropYear: new Date().getFullYear(),
        cropVarietyId: cropVarietyId === this.cropField?.cropVarietyId ? null : cropVarietyId,
      };
      this.store.dispatch(
        postGeneralQuestions({
          answers: answersPayload,
        }),
      );

      this.unsavedChangesService.markFormCompleted();
    }
  }

  private handleCropFieldResult(cropfieldWithStatus: TgaCropFieldWithStatus | null) {
    if (!cropfieldWithStatus && this.cropYear !== null) {
      this.selectedCropYear$.pipe(take(1)).subscribe((cropYear) => {
        this.store.dispatch(loadCropFields({ cropYear }));
      });
    }
    this.cropField = cropfieldWithStatus?.cropField ?? undefined;
  }

  private mapCropVarieties(varieties: CropVarietiesByCropId[], cropId?: number) {
    if (!cropId) return [];
    const cropVarieties = varieties?.find((cv) => cv.cropId === cropId)?.cropVarieties ?? [];
    return cropVarieties.map((variety) => ({
      key: variety.id.toString(),
      value: variety.name,
    }));
  }

  private initializeParamsAndQuestions() {
    this.params$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap((params) =>
          this.questions$.pipe(
            take(1),
            tap(() => this.checkAndFetchMissingQuestions(params)),
            map(() => params),
          ),
        ),
      )
      .subscribe((params) => {
        this.cropFieldId = params.cropFieldId;
        this.cropYear = params.cropYear;
      });

    this.questions$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((questions) => questions.length > 0),
      )
      .subscribe((questions) => {
        this.generalQuestions.set(questions);
      });
  }

  private checkAndFetchMissingQuestions(params: { cropFieldId: number; cropYear: number }) {
    this.selectedCropYear$.pipe(take(1)).subscribe((cropYear) => {
      this.store.dispatch(
        fetchGeneralQuestions({
          cropFieldId: params.cropFieldId,
          cropYear,
        }),
      );
    });
  }

  private loadCropVarieties(cropId: number) {
    if (cropId) {
      this.store.dispatch(loadCropVarietiesByCropIdRequest({ cropIds: [cropId] }));
    }
  }
}
